/**
 * A card on the board.
 *
 * ## Two drag affordances, on purpose
 *
 * - **Pointer**: the whole card is a drag handle. That is what people expect from
 *   a Kanban board, and `PointerSensor`'s activation distance (6 px, configured in
 *   `BoardView`) is what stops a click from becoming a drag - a click opens the
 *   card, a press-and-move drags it.
 * - **Keyboard**: a dedicated grip button carries the `dnd-kit` attributes and
 *   the full listener set. Space or Enter on the grip starts a keyboard drag;
 *   arrow keys move; Space drops; Escape cancels. `dnd-kit` announces each step
 *   through the live region configured on `DndContext`, so a screen reader user
 *   hears "Card moved to In progress, position 2 of 5" rather than silence.
 *
 * The two are separate because they conflict. `dnd-kit`'s keyboard activator is
 * `onKeyDown` for Space and Enter - the same keys that activate a focused card. If
 * the card root carried both, pressing Enter would start a drag and never open the
 * drawer, and the failure would look like a broken button rather than a drag bug.
 *
 * ## The accessible name is composed, not inherited
 *
 * The open button's name is built from the card's real fields rather than from its
 * rendered children. Letting the contents become the name would read the label
 * chips, the due-date badge and the assignee's initials as a run-on string, and
 * the initials in particular ("PS" for Priya Shah) mean nothing read aloud. The
 * visual tree is `aria-hidden` and the name is explicit, so the two are
 * independent: a layout change cannot silently change what is announced.
 *
 * ## Why the card does not re-render during a drag
 *
 * The drag preview lives in `BoardProvider`'s preview context and is applied by
 * `materialise`, so during a drag the columns re-render and the cards do not -
 * each card's props are unchanged. That is what keeps a drag smooth on a board
 * with two hundred cards.
 */

import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Card, PublicUser } from '@slate/shared';
import { Avatar } from './Avatar.js';
import { Icon } from './ui.js';
import { PRIORITY_BG_CLASS, PRIORITY_LABEL, formatDueDate, isOverdue } from '../lib/format.js';
import { toPlainText } from '../lib/markdown.js';

/**
 * `dnd-kit` types every entry in `listeners` as `Function`, because the map is
 * built per sensor and a sensor may install any handler it likes. React needs the
 * concrete handler type for the prop it is spread onto, so the one listener this
 * component uses is narrowed at the point of use. A cast through a named type
 * rather than `as never`/`any`: if `dnd-kit` ever changes the activator's
 * signature, this line stops compiling instead of silently passing a
 * wrongly-typed handler to React.
 */
type PointerDownHandler = (event: ReactPointerEvent<HTMLElement>) => void;

export interface CardTileProps {
  card: Card;
  /** Resolved from the board's member list. `null` when unassigned. */
  assignee: PublicUser | null;
  /** True while an optimistic operation for this card is unconfirmed. */
  isPending: boolean;
  /** Names of other people currently typing a comment on this card. */
  typingNames: string[];
  onOpen(cardId: string): void;
}

const MAX_VISIBLE_LABELS = 3;

function accessibleName(card: Card, assignee: PublicUser | null, typingNames: string[]): string {
  const parts = [card.title];
  if (card.priority !== 'none') parts.push(PRIORITY_LABEL[card.priority]);
  if (card.dueDate !== null) parts.push(`due ${formatDueDate(card.dueDate)}`);
  if (assignee !== null) parts.push(`assigned to ${assignee.name}`);
  if (card.labels.length > 0) parts.push(`labels: ${card.labels.map((label) => label.name).join(', ')}`);
  if (typingNames.length > 0) parts.push(`${typingNames.join(' and ')} typing`);
  return parts.join(', ');
}

export function CardTile({ card, assignee, isPending, typingNames, onOpen }: CardTileProps): ReactNode {
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id: card.id,
    data: { type: 'card', columnId: card.columnId },
  });

  return (
    <article
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // The dragged card is dimmed in place; `DragOverlay` renders the visible
        // copy that follows the pointer. Keeping the original in the tree rather
        // than unmounting it is what lets `dnd-kit` measure the gap and animate
        // the surrounding cards out of the way.
        opacity: isDragging ? 0.35 : 1,
      }}
      // Only the pointer activator, not the whole listener map - see the module
      // docblock. The keyboard activator is `onKeyDown` for Space and Enter, and
      // that lives on the grip button instead.
      onPointerDown={listeners?.['onPointerDown'] as PointerDownHandler | undefined}
      className={[
        'group/card relative rounded-card border bg-surface shadow-sm transition-colors',
        isPending ? 'border-dashed border-border-strong' : 'border-border',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => onOpen(card.id)}
        aria-label={accessibleName(card, assignee, typingNames)}
        className="block w-full cursor-pointer rounded-card p-2.5 text-left"
      >
        <span aria-hidden="true" className="flex flex-col gap-1.5">
          {card.labels.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {card.labels.slice(0, MAX_VISIBLE_LABELS).map((label) => (
                <span
                  key={label.id}
                  className="rounded-sm px-1.5 py-0.5 text-[10px] leading-4 font-medium"
                  style={{ backgroundColor: `${label.colour}22`, color: label.colour }}
                >
                  {label.name}
                </span>
              ))}
              {card.labels.length > MAX_VISIBLE_LABELS && (
                <span className="text-[10px] leading-4 text-ink-subtle">
                  +{card.labels.length - MAX_VISIBLE_LABELS}
                </span>
              )}
            </span>
          )}

          <span className="flex items-start gap-1.5">
            {card.priority !== 'none' && (
              <span className={`mt-[5px] h-2 w-2 shrink-0 rounded-full ${PRIORITY_BG_CLASS[card.priority]}`} />
            )}
            <span className="clamp-2 text-sm leading-snug font-medium text-ink">{card.title}</span>
          </span>

          {card.description !== null && card.description.trim() !== '' && (
            <span className="clamp-2 text-xs leading-snug text-ink-muted">{toPlainText(card.description, 120)}</span>
          )}

          {(card.dueDate !== null || assignee !== null || typingNames.length > 0) && (
            <span className="mt-0.5 flex items-center gap-2">
              {card.dueDate !== null && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] ${
                    isOverdue(card.dueDate) ? 'bg-danger-soft text-danger' : 'bg-sunken text-ink-muted'
                  }`}
                >
                  {formatDueDate(card.dueDate)}
                </span>
              )}
              {typingNames.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-subtle">
                  <Icon name="comment" size={11} />
                  {typingNames.length === 1 ? `${typingNames[0]} is typing` : `${typingNames.length} typing`}
                </span>
              )}
              {assignee !== null && (
                <span className="ml-auto">
                  <Avatar name={assignee.name} colour={assignee.colour} size={20} title={assignee.name} />
                </span>
              )}
            </span>
          )}
        </span>
      </button>

      {/* The keyboard drag handle. Always in the tab order - a handle that only
          appears on hover is unreachable by keyboard, which is the usual way a
          "drag and drop with a11y support" claim turns out to be false. It is
          visually hidden until the card is hovered or the handle is focused. */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Move ${card.title}`}
        className="absolute top-1.5 right-1.5 rounded p-0.5 text-ink-subtle opacity-0 transition-opacity group-hover/card:opacity-100 hover:bg-sunken hover:text-ink focus-visible:opacity-100"
      >
        <Icon name="grip" size={14} />
      </button>
    </article>
  );
}

/**
 * The card as rendered inside `DragOverlay`.
 *
 * A separate presentation-only component rather than `CardTile` with the sortable
 * hooks disabled: `useSortable` registers its node with the drag context, and
 * registering a second node for the same id would make `dnd-kit` measure the
 * overlay as if it were the original, producing a drag that fights itself.
 */
export function CardTileOverlay({ card, assignee }: { card: Card; assignee: PublicUser | null }): ReactNode {
  return (
    <div className="w-[17rem] rotate-1 rounded-card border border-border-strong bg-raised p-2.5 shadow-lift">
      {card.labels.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {card.labels.slice(0, MAX_VISIBLE_LABELS).map((label) => (
            <span
              key={label.id}
              className="rounded-sm px-1.5 py-0.5 text-[10px] leading-4 font-medium"
              style={{ backgroundColor: `${label.colour}22`, color: label.colour }}
            >
              {label.name}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-start gap-1.5">
        {card.priority !== 'none' && (
          <span className={`mt-[5px] h-2 w-2 shrink-0 rounded-full ${PRIORITY_BG_CLASS[card.priority]}`} />
        )}
        <h4 className="clamp-2 text-sm leading-snug font-medium text-ink">{card.title}</h4>
      </div>
      {assignee !== null && (
        <div className="mt-2 flex justify-end">
          <Avatar name={assignee.name} colour={assignee.colour} size={20} title={assignee.name} />
        </div>
      )}
    </div>
  );
}
